/** Copyright 2024 SAP SE or an SAP affiliate company. All rights reserved. */

import { ComboBox, ComboBoxItem, FlexBox, Text, Ui5CustomEvent, ComboBoxDomRef } from '@ui5/webcomponents-react';
import React, { useEffect, useState } from 'react';
import { uiFullWidth } from '@/Styling/Theme/Theme';
import { useDispatch, useSelector } from 'react-redux';
import { actions } from '@/store/Products';
import { UserSettings } from '@/types';
import { useTranslations } from '@/hooks/useTranslations';
import ComboBoxFilter from '@ui5/webcomponents/dist/types/ComboBoxFilter';
import { freApi } from '@/store/apis';
import ValueState from '@ui5/webcomponents-base/dist/types/ValueState';

export const CrmSwitch: React.FC = () => {
    const userSettings: UserSettings = useSelector((state: any) => state.productList.userSettings);
    const { data: userInfo } = freApi.useGetUserInfoQuery();
    const dispatch = useDispatch<any>();
    const { t } = useTranslations();
    const [crmList, setCrmist] = useState<string[]>([]);
    const [valueState, setValueState] = useState<ValueState>(ValueState.None);

    useEffect(() => {
        if (userInfo?.supported_crm_list) {
            setCrmist(userInfo.supported_crm_list.split(',').map(item => item.trim()));
        }
    }, [userInfo]);

    const isValidCrmId = (value: string): boolean => {
        if (value === ' ') return false;
        return crmList.some(id => id.toString() === value);
    };

    const handleOnInput = (event: Ui5CustomEvent<ComboBoxDomRef, never>): void => {
        const inputValue = event.target.value;

        const isValid = isValidCrmId(inputValue);
        setValueState(isValid ? ValueState.None : ValueState.Negative);

        dispatch(
            actions.setUserSettings({
                ...userSettings,
                isValid: isValid,
            })
        );
    };

    const onChange = (event: any): void => {
        const newCrmId = event.detail.item.getAttribute('data-code');
        setValueState(ValueState.None);

        dispatch(
            actions.setUserSettings({
                ...userSettings,
                crmId: userInfo?.crm_account_id,
                selectedCRMId: newCrmId,
                isValid: true, // Always valid when selected from dropdown
            })
        );
    };

    const getCrmAccountValue = (userInfo: { crm_account_id?: string }, crmList: string[]): string => {
        if (userInfo?.crm_account_id) {
            return userInfo.crm_account_id;
        }

        if (crmList.length > 0) {
            return crmList[0];
        }

        return '';
    };

    return (
        <FlexBox style={{ width: uiFullWidth.width }}>
            <ComboBox
                onInput={handleOnInput}
                onSelectionChange={onChange}
                style={{ width: '100%' }}
                value={getCrmAccountValue(userInfo ?? {}, crmList)}
                data-testid="crmId-switch-wrapper"
                filter={ComboBoxFilter.StartsWith}
                valueState={valueState}
                valueStateMessage={<Text>{t('INVALID_ENTRY')}</Text>}
                placeholder={t('SELECT_CRM')}
                readonly={crmList.length === 0}
            >
                {crmList.map(code => (
                    <ComboBoxItem key={code} text={code.toString()} data-code={code} />
                ))}
            </ComboBox>
        </FlexBox>
    );
};

export default CrmSwitch;
